'use client';

import React, { useState, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ArrowRight, X, FileText, LogIn } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { validateCPF, validateCNPJ } from '@/lib/mockData';

interface SignupFormData {
  name: string;
  email: string;
  company: string;
  docType: 'CPF' | 'CNPJ';
  docNumber: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

type PdfDialogType = 'termos' | 'privacidade' | null;

function PdfViewerDialog({
  open,
  onClose,
  type,
}: {
  open: boolean;
  onClose: () => void;
  type: PdfDialogType;
}) {
  if (!open || !type) return null;

  const title = type === 'termos' ? 'Termos de Uso' : 'Política de Privacidade';
  const fileName =
    type === 'termos' ?'TERMOS_DE_USO-1779073825196.pdf' :'POLITICA_DE_PRIVACIDADE-1779073825196.pdf';

  const embedSrc =
    type === 'termos' ?'/api/docs/termos' :'/api/docs/privacidade';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-3xl h-[85vh] bg-[#0a1628] rounded-2xl border border-white/10 flex flex-col shadow-2xl overflow-hidden">
        {/* Dialog header */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#071020] border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-[#60a5fa]" />
            <span className="text-sm font-semibold text-white">{title}</span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Fechar"
          >
            <X size={14} className="text-white" />
          </button>
        </div>

        {/* PDF embed */}
        <div className="flex-1 overflow-hidden">
          <object
            data={embedSrc}
            type="application/pdf"
            className="w-full h-full"
            aria-label={title}
          >
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(`https://rd2sign6786.builtwithrocket.new${embedSrc}`)}&embedded=true`}
              className="w-full h-full border-0"
              title={title}
            />
          </object>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-[#071020] border-t border-white/10 shrink-0 flex items-center justify-between">
          <span className="text-xs text-white/40">{title} — RD2Sign</span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-[#1a56db] hover:bg-[#1e40af] text-white text-xs font-semibold rounded-lg transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function CadastroContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfDialog, setPdfDialog] = useState<PdfDialogType>(null);
  const [alreadyPurchased, setAlreadyPurchased] = useState(false);

  const plan = searchParams.get('plan') || '';

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignupFormData>({ defaultValues: { docType: 'CPF' } });

  const docType = watch('docType');
  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      // ── Step 0: Pre-check if email already exists BEFORE calling signUp ──
      try {
        const preCheckRes = await fetch('/api/check-email-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: data.email }),
        });
        const preCheckResult = await preCheckRes.json();

        if (preCheckResult.userExists) {
          // User already exists in Supabase
          if (preCheckResult.hasCompletedPurchase) {
            // Has active subscription — show "Conta já ativa" modal
            setAlreadyPurchased(true);
            setIsLoading(false);
            return;
          } else {
            // Registered but no completed purchase — show inline error
            setError('email', {
              message: 'Este e-mail já está cadastrado. Faça login para continuar.',
            });
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // If pre-check fails, proceed normally (signUp will catch duplicates)
      }

      // Use the app's actual origin so the confirmation link returns to this app,
      // NOT NEXT_PUBLIC_SITE_URL which points to the marketing/landing site.
      const appOrigin = 'https://rd2sign6786.builtwithrocket.new';
      const callbackParams = new URLSearchParams({ onboarding: '1' });
      if (plan) callbackParams.set('plan', plan);
      const redirectTo = `${appOrigin}/auth/callback?${callbackParams.toString()}`;

      await signUp(data.email, data.password, {
        fullName: data.name,
        company: data.company,
        docType: data.docType,
        docNumber: data.docNumber,
        emailRedirectTo: redirectTo,
        plan: plan || undefined,
      });

      // NOTE: Do NOT call /api/auth/resend-confirmation here.
      // The /api/auth/signup route already sends the confirmation email via Resend.
      // Calling it again would result in two identical emails being delivered.

      const params = new URLSearchParams({ email: data.email });
      if (plan) params.set('plan', plan);
      params.set('onboarding', '1');
      router.push(`/email-confirmation?${params.toString()}`);
    } catch (err: any) {
      const message = err?.message || '';
      if (message === 'EMAIL_ALREADY_REGISTERED' || message === 'User already registered') {
        // Check if user already completed a purchase
        try {
          const res = await fetch('/api/check-email-subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: data.email }),
          });
          const result = await res.json();

          if (result.hasCompletedPurchase) {
            // User already has a completed subscription — block and invite to login
            setAlreadyPurchased(true);
          } else {
            // User registered but hasn't completed purchase — allow to continue
            // Redirect them to the next step in the onboarding flow
            const params = new URLSearchParams({ email: data.email });
            if (plan) params.set('plan', plan);
            params.set('onboarding', '1');
            params.set('existing_user', '1');
            router.push(`/email-confirmation?${params.toString()}`);
          }
        } catch {
          // If check fails, fall back to generic error
          setError('email', { message: 'Este e-mail já está cadastrado. Tente fazer login.' });
        }
      } else {
        toast.error(message || 'Erro ao criar conta. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateDoc = (value: string) => {
    if (docType === 'CPF') {
      const cleaned = value.replace(/\D/g, '');
      return validateCPF(cleaned) || 'CPF inválido';
    }
    // For CNPJ: strip only formatting chars (dots, slashes, hyphens) — keep letters for alphanumeric CNPJ
    return validateCNPJ(value) || 'CNPJ inválido';
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3.5 py-2.5 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
    focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
    ${hasError ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`;

  return (
    <>
      <PdfViewerDialog
        open={pdfDialog !== null}
        onClose={() => setPdfDialog(null)}
        type={pdfDialog}
      />

      {/* Already purchased — block modal */}
      {alreadyPurchased && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-[#0a1628] rounded-2xl border border-white/10 shadow-2xl p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#1a56db]/20 border border-[#1a56db]/40 flex items-center justify-center mx-auto mb-5">
              <LogIn size={26} className="text-[#60a5fa]" />
            </div>
            <h2 className="text-xl font-extrabold text-white mb-2">Conta já ativa</h2>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">
              Este e-mail já possui uma assinatura ativa na plataforma RD2Sign.
              Faça login para acessar sua conta.
            </p>
            <div className="flex flex-col gap-3">
              <Link
                href={`/sign-up-login-screen${plan ? `?plan=${plan}` : ''}`}
                className="w-full py-3 bg-[#1a56db] hover:bg-[#1e40af] text-white font-bold rounded-xl transition-all shadow-lg shadow-[#1a56db]/30 flex items-center justify-center gap-2 text-sm"
              >
                <LogIn size={16} />
                Fazer Login
              </Link>
              <button
                onClick={() => setAlreadyPurchased(false)}
                className="w-full py-2.5 text-white/50 hover:text-white text-sm transition-colors"
              >
                Usar outro e-mail
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-[#050d1a] text-white font-sans">
        {/* Header */}
        <header className="border-b border-white/10 bg-[#050d1a]/95 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-3">
              <Image
                src="/assets/images/rd2_icon-1777353885177.png"
                alt="RD2Sign logo"
                width={32}
                height={32}
                className="rounded-lg"
              />
              <span className="text-lg font-bold text-white">RD2Sign</span>
            </Link>
            {/* Step indicator — 4 steps (Termo removed) */}
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <span className="w-6 h-6 rounded-full bg-[#1a56db] text-white font-bold flex items-center justify-center text-xs">1</span>
              <span className="text-white font-semibold">Cadastro</span>
              <div className="w-8 h-px bg-white/20" />
              <span className="w-6 h-6 rounded-full bg-white/10 text-white/40 font-bold flex items-center justify-center text-xs">2</span>
              <span className="text-white/40">E-mail</span>
              <div className="w-8 h-px bg-white/20" />
              <span className="w-6 h-6 rounded-full bg-white/10 text-white/40 font-bold flex items-center justify-center text-xs">3</span>
              <span className="text-white/40">Plano</span>
              <div className="w-8 h-px bg-white/20" />
              <span className="w-6 h-6 rounded-full bg-white/10 text-white/40 font-bold flex items-center justify-center text-xs">4</span>
              <span className="text-white/40">Pagamento</span>
            </div>
          </div>
        </header>

        <main className="max-w-lg mx-auto px-6 py-10">
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/20 border border-[#1a56db]/40 rounded-full text-xs font-semibold text-[#60a5fa] mb-4">
              <ArrowRight size={12} />
              Passo 1 de 4 — Criar Conta
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-2">Assinar Plano RD2Sign</h1>
            <p className="text-white/50 text-sm">
              Crie sua conta para iniciar o processo de assinatura do plano.
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0a1628] p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-[13px] font-600 text-white/80 mb-1.5">Nome completo</label>
                <input
                  type="text"
                  placeholder="Seu nome completo"
                  {...register('name', { required: 'Nome é obrigatório', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })}
                  className={inputClass(!!errors.name)}
                />
                {errors.name && <p className="text-red-400 text-[12px] mt-1">{errors.name.message}</p>}
              </div>

              <div>
                <label className="block text-[13px] font-600 text-white/80 mb-1.5">E-mail</label>
                <input
                  type="email"
                  placeholder="seu@empresa.com.br"
                  {...register('email', {
                    required: 'E-mail é obrigatório',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido' },
                  })}
                  className={inputClass(!!errors.email)}
                />
                {errors.email && <p className="text-red-400 text-[12px] mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <label className="block text-[13px] font-600 text-white/80 mb-1.5">Empresa / Organização</label>
                <input
                  type="text"
                  placeholder="Nome da sua empresa"
                  {...register('company', { required: 'Empresa é obrigatória' })}
                  className={inputClass(!!errors.company)}
                />
                {errors.company && <p className="text-red-400 text-[12px] mt-1">{errors.company.message}</p>}
              </div>

              <div>
                <label className="block text-[13px] font-600 text-white/80 mb-1.5">Documento</label>
                <div className="flex gap-2">
                  <select
                    {...register('docType')}
                    className="px-3 py-2.5 rounded-lg border border-white/10 bg-white/5 text-white text-[14px] outline-none focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db] w-24 shrink-0"
                  >
                    <option value="CPF" className="bg-[#0a1628] text-white">CPF</option>
                    <option value="CNPJ" className="bg-[#0a1628] text-white">CNPJ</option>
                  </select>
                  <input
                    type="text"
                    placeholder={docType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00 ou AB.CDE.FGH/0001-XX'}
                    {...register('docNumber', { required: `${docType} é obrigatório`, validate: validateDoc })}
                    className={`flex-1 px-3.5 py-2.5 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
                      focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
                      ${errors.docNumber ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
                  />
                </div>
                {errors.docNumber && <p className="text-red-400 text-[12px] mt-1">{errors.docNumber.message}</p>}
              </div>

              <div>
                <label className="block text-[13px] font-600 text-white/80 mb-1.5">Senha</label>
                <p className="text-[11px] text-white/40 mb-1.5">Mínimo 8 caracteres, com letras, números e caractere especial (!@#$%...)</p>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Crie uma senha forte"
                    {...register('password', {
                      required: 'Senha é obrigatória',
                      minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                      pattern: { value: /(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~])/, message: 'Use letras, números e ao menos um caractere especial (!@#$%...)' },
                    })}
                    className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
                      focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
                      ${errors.password ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
                  />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && <p className="text-red-400 text-[12px] mt-1">{errors.password.message}</p>}
              </div>

              <div>
                <label className="block text-[13px] font-600 text-white/80 mb-1.5">Confirmar senha</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repita a senha"
                    {...register('confirmPassword', {
                      required: 'Confirmação é obrigatória',
                      validate: v => v === password || 'As senhas não coincidem',
                    })}
                    className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
                      focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
                      ${errors.confirmPassword ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
                  />
                  <button type="button" onClick={() => setShowConfirm(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-400 text-[12px] mt-1">{errors.confirmPassword.message}</p>}
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  {...register('terms', { required: 'Aceite os termos para continuar' })}
                  className="w-4 h-4 mt-0.5 rounded border-white/20 bg-white/5 cursor-pointer"
                />
                <label htmlFor="terms" className="text-[12.5px] text-white/50 cursor-pointer leading-relaxed">
                  Concordo com os{' '}
                  <button
                    type="button"
                    onClick={() => setPdfDialog('termos')}
                    className="text-[#60a5fa] hover:underline focus:outline-none"
                  >
                    Termos de Uso
                  </button>{' '}
                  e a{' '}
                  <button
                    type="button"
                    onClick={() => setPdfDialog('privacidade')}
                    className="text-[#60a5fa] hover:underline focus:outline-none"
                  >
                    Política de Privacidade
                  </button>
                </label>
              </div>
              {errors.terms && <p className="text-red-400 text-[12px]">{errors.terms.message}</p>}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-[#1a56db] hover:bg-[#1e40af] text-white rounded-lg font-600 text-[14px]
                  shadow-lg shadow-[#1a56db]/30 active:scale-[0.98] transition-all duration-150
                  disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin" />Criando conta...</>
                ) : (
                  <><ArrowRight size={16} />Criar Conta e Continuar</>
                )}
              </button>
            </form>

            <p className="text-center text-[13px] text-white/50 mt-5">
              Já tem uma conta?{' '}
              <Link href="/sign-up-login-screen" className="text-[#60a5fa] font-600 hover:underline">
                Fazer login
              </Link>
            </p>
          </div>
        </main>
      </div>
    </>
  );
}

export default function CadastroPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#1a56db]" />
      </div>
    }>
      <CadastroContent />
    </Suspense>
  );
}
